import { createSlice } from "@reduxjs/toolkit";
import {
  createExam,
  deleteExam,
  filterExam,
  getAtcExams,
  getAtcUsers,
  getExams,
  updateExam,
} from "../apiActions/examActions";

import handleApiMessage from "../../utils/handleApiError";
import { notification } from "antd";
import successModal from "../../components/ConfirmationModal";
import {
  getExaminerScheduleExamInstances,
  getUserScheduleExamInstances,
  getUserSubmittedExamInstances,
} from "../apiActions/examinerActions";

export type Exam = {
  id: string;
  created_at: string;
  name: string;
  description: string;
  instruction: string;
  duration: number;
  slots: number[];
  slot_limit: number;
  expire_by: number;
  amount: number;
  logo: string;
  file: string;
  product: {
    id: string;
    created_at: string;
    name: string;
  };
  questions: {
    id: string;
    created_at: string;
    category: {
      id: string;
      created_at: string;
      name: string;
    };
    description: string;
    type: string;
    answer: string;
    score: number;
    exam_id: string;
    option: string | null;
  }[];
  report_score: number;
  report: boolean;
  hidden: boolean;
  status?: string;
  lab_available: boolean;
  use_xvda?: boolean;
};

interface InitialState {
  loading: {
    getExam: boolean;
    createExam: boolean;
    updateExam: boolean;
    deleteExam: boolean;
  };
  deleteLoading: boolean;
  error: null | string;
  exams: Exam[];
  atcUsers: Exam[];
  atcExams: Exam[];
  userExams: Exam[];
  examFilter: {
    from: number | null;
    to: number | null;
  };
  isExamRunning: boolean;
  examTimeLeft: number;
  /* Lab state of the exam currently being taken. The header's Reset Lab /
     Download VPN Config buttons only make sense on a paper that actually has a
     lab, so they hang off labAvailable rather than off isExamRunning. */
  examLabAvailable: boolean;
  examLabStatus: string;
}

const initialState: InitialState = {
  exams: [],
  atcUsers: [],
  atcExams: [],
  userExams: [],
  error: null,
  loading: {
    getExam: false,
    updateExam: false,
    deleteExam: false,
    createExam: false,
  },
  deleteLoading: false,
  examFilter: {
    from: null,
    to: null,
  },
  isExamRunning: false,
  examTimeLeft: 0,
  examLabAvailable: false,
  examLabStatus: "",
};

const examSlice = createSlice({
  name: "exam",
  initialState,
  reducers: {
    initExamRunning(state) {
      state.isExamRunning = true;
    },

    setExamLeftTime(state, action) {
      state.examTimeLeft = action.payload.time;
    },

    setExamLab(state, action) {
      state.examLabAvailable = Boolean(action.payload?.available);
      state.examLabStatus = action.payload?.status ?? "";
    },

    stopExamRunning(state) {
      state.isExamRunning = false;
      /* Clear the lab flags with the exam: they are per-instance, and a stale
         `true` would put the lab buttons on the next paper's header. */
      state.examLabAvailable = false;
      state.examLabStatus = "";
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getExams.pending, (state: InitialState) => {
        state.loading.getExam = true;
        state.error = null;
      })
      .addCase(getExams.fulfilled, (state: InitialState, action) => {
        state.loading.getExam = false;
        state.error = null;
        state.exams = action.payload;
      })
      .addCase(getExams.rejected, (state: InitialState, action) => {
        state.loading.getExam = false;
      })
      .addCase(getAtcUsers.pending, (state: InitialState) => {
        state.loading.getExam = true;
        state.error = null;
      })
      .addCase(getAtcUsers.fulfilled, (state: InitialState, action) => {
        state.loading.getExam = false;
        state.error = null;
        state.atcUsers = action.payload;
      })
      .addCase(getAtcUsers.rejected, (state: InitialState, action) => {
        state.loading.getExam = false;
      })
      .addCase(getAtcExams.pending, (state: InitialState) => {
        state.loading.getExam = true;
        state.error = null;
      })
      .addCase(getAtcExams.fulfilled, (state: InitialState, action) => {
        state.loading.getExam = false;
        state.error = null;
        state.atcExams = action.payload;
      })
      .addCase(getAtcExams.rejected, (state: InitialState, action) => {
        state.loading.getExam = false;
      })
      .addCase(createExam.pending, (state: InitialState) => {
        state.loading.createExam = true;
        state.error = null;
      })
      .addCase(createExam.fulfilled, (state: InitialState, action) => {
        state.loading.createExam = false;
        state.error = null;
        successModal.show({ text: "Exam Created" });
      })
      .addCase(createExam.rejected, (state: InitialState, action) => {
        state.loading.createExam = false;
        notification.error(action.payload as any);
      })
      .addCase(deleteExam.pending, (state: InitialState) => {
        state.loading.deleteExam = true;
        state.error = null;
      })
      .addCase(deleteExam.fulfilled, (state: InitialState, action) => {
        state.loading.deleteExam = false;
        state.error = null;
      })
      .addCase(deleteExam.rejected, (state: InitialState, action) => {
        state.loading.deleteExam = false;
        state.error = action.payload as string;
        handleApiMessage(state.error, "error");
      })
      .addCase(updateExam.pending, (state: InitialState) => {
        state.loading.updateExam = true;
        state.error = null;
      })
      .addCase(updateExam.fulfilled, (state: InitialState, action) => {
        state.loading.updateExam = false;
        state.error = null;
        successModal.show({ text: "Exam Updated" });
      })
      .addCase(updateExam.rejected, (state: InitialState, action) => {
        state.loading.updateExam = false;
        state.error = action.payload as string;
        notification.error(action.payload as any);
      })
      .addCase(filterExam.pending, (state: InitialState, action) => {
        state.loading.getExam = true;
        state.error = null;
      })
      .addCase(filterExam.fulfilled, (state: InitialState, action) => {
        state.loading.getExam = false;
        state.error = null;
        state.exams = action.payload;
      })
      .addCase(filterExam.rejected, (state: InitialState, action) => {
        state.loading.getExam = false;
        state.error = action.payload as string;
        notification.error(action.payload as any);
      })
      .addCase(
        getExaminerScheduleExamInstances.pending,
        (state: InitialState, action) => {
          state.loading.getExam = true;
          state.error = null;
        }
      )
      .addCase(
        getExaminerScheduleExamInstances.fulfilled,
        (state: InitialState, action) => {
          state.loading.getExam = false;
          state.error = null;
          state.userExams = action.payload;
        }
      )
      .addCase(
        getExaminerScheduleExamInstances.rejected,
        (state: InitialState, action) => {
          state.loading.getExam = false;
          state.error = action.payload as string;
          notification.error(action.payload as any);
        }
      )
      .addCase(
        getUserScheduleExamInstances.pending,
        (state: InitialState, action) => {
          state.loading.getExam = true;
          state.error = null;
        }
      )
      .addCase(
        getUserScheduleExamInstances.fulfilled,
        (state: InitialState, action) => {
          state.loading.getExam = false;
          state.error = null;
          state.userExams = action.payload;
        }
      )
      .addCase(
        getUserScheduleExamInstances.rejected,
        (state: InitialState, action) => {
          state.loading.getExam = false;
          state.error = action.payload as string;
          notification.error(action.payload as any);
        }
      )
      .addCase(
        getUserSubmittedExamInstances.pending,
        (state: InitialState, action) => {
          state.loading.getExam = true;
          state.error = null;
        }
      )
      .addCase(
        getUserSubmittedExamInstances.fulfilled,
        (state: InitialState, action) => {
          state.loading.getExam = false;
          state.error = null;
          state.userExams = action.payload;
        }
      )
      .addCase(
        getUserSubmittedExamInstances.rejected,
        (state: InitialState, action) => {
          state.loading.getExam = false;
          state.error = action.payload as string;
          notification.error(action.payload as any);
        }
      );
  },
});

export const {
  initExamRunning,
  stopExamRunning,
  setExamLeftTime,
  setExamLab,
} = examSlice.actions;
export default examSlice.reducer;
